import { motion } from 'framer-motion';

import { BlockedScreen } from '@/screens/BlockedScreen';
import { ClosedScreen } from '@/screens/ClosedScreen';
import { GateScreen } from '@/screens/GateScreen';
import { LobbyScreen } from '@/screens/LobbyScreen';
import { CardScreen } from '@/screens/game/CardScreen';
import { FinishedScreen } from '@/screens/game/FinishedScreen';
import { HandoffScreen } from '@/screens/game/HandoffScreen';
import { PauseScreen } from '@/screens/game/PauseScreen';
import { TurnScreen } from '@/screens/game/TurnScreen';
import type { Screen } from '@/store/game';
import { selectScreen, useGame } from '@/store/game';

/**
 * Экран выводится из selectScreen — сама App ничего не решает про правила
 * игры, только раскладывает уже готовый ответ по компонентам.
 */
export function App() {
  const blocker = useGame((s) => s.blocker);
  const closedReason = useGame((s) => s.closedReason);
  const screen = useGame(selectScreen);

  // Тупик перекрывает всё: и гейт, и партию. Из него нет выхода внутри
  // приложения, поэтому и выбирать между экранами уже не из чего.
  if (blocker) return <BlockedScreen reason={blocker} />;

  // Комната закрыта, а состояние партии в сторе ещё лежит: без этого экрана
  // человек смотрел бы в живой с виду стол с мёртвыми кнопками. Завершённая
  // партия — исключение: у неё свой экран, и `closed` там ожидаем.
  if (closedReason && screen !== 'finished') return <ClosedScreen />;

  // Смена экрана — короткий вход, без ухода предыдущего: экран партии
  // обязан появляться мгновенно, а не после чужой анимации. Ключ по экрану
  // заставляет React смонтировать новый узел, а Motion — его показать.
  return (
    <motion.div
      key={screen}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {pick(screen)}
    </motion.div>
  );
}

function pick(screen: Screen) {
  switch (screen) {
    case 'gate':
      return <GateScreen />;
    case 'lobby':
      return <LobbyScreen />;
    case 'handoff':
      return <HandoffScreen />;
    case 'turn':
      return <TurnScreen />;
    case 'card':
      return <CardScreen />;
    case 'pause':
      return <PauseScreen />;
    case 'finished':
      return <FinishedScreen />;
  }
}
